/* eslint-disable max-lines */

import tokens from '@kluein/design-tokens';
import { ActionMenu, Badge, Box, Skeleton, Text } from '@kluein/klue-ui';
import {
  Check,
  DotsThreeOutlineVertical,
  WarningCircle,
} from '@phosphor-icons/react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

import { Logo } from 'components/common/logo';
import { useAuth } from 'contexts/auth';
import { useNotifier, NotifierType } from 'hooks/use-notifier';
import { useRedirect } from 'hooks/use-redirect';
import { useUpdateProfileDraftStateMutation } from 'store/api/profiles';
import { useUpdateRivalGroupMutation } from 'store/api/rival-groups';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

import { RivalGroupActionItem } from './RivalGroupActionItem';

import type { RivalType } from 'api/api.types';
import type { AuthContextType } from 'contexts/auth/auth.types';

// TODO Improve the solution such that the heights need not be hardcoded?
export const HEIGHT_WHEN_RIVAL_LINK_SHOWN = '215px';
export const HEIGHT_WHEN_RIVAL_LINK_HIDDEN = '165px';

export type RivalGroupItemType = {
  rival: RivalType;
  rivalGroupId?: number;
  order?: number;
  rowOrder?: number;
};

const StyledMainBox = styled(Box)`
  ${({ theme }) => css`
    &:hover {
      box-shadow: ${theme.global.elevation.light.small};
      border-color: ${tokens.color.neutral.mediumgrey.main};
    }
  `}
`;

export default function RivalGroupItem({
  rival,
  rivalGroupId,
  order,
  rowOrder,
}: RivalGroupItemType) {
  const [zIndex, setIndex] = useState<number | undefined>(undefined);
  const { t } = useTranslation();
  const {
    companyLinkDefault,
    company,
    isCompanyLandingPageEnabled,
    isCurator,
  } = useAuth();
  const { notify } = useNotifier();
  const { getRivalRedirectLink } = useRedirect();
  const [updateProfileDraftState, { isLoading: isUpdatingProfile }] =
    useUpdateProfileDraftStateMutation();
  const [updateRivalGroup, { isLoading: isUpdatingRivalGroup }] =
    useUpdateRivalGroupMutation();

  const { battlecardsCount = 0, cardsCount = 0 } = rival || {};

  const defaultRedirect: AuthContextType['companyLinkDefault'] = useMemo(() => {
    if (company?.companyData?.forceProfileLinksOnDashboard) {
      return 'profile';
    }

    if (companyLinkDefault === 'landing_page' && rival?.supportedRivalId) {
      return 'landing_page';
    }

    if (battlecardsCount) {
      return 'battlecard';
    }

    if (isCompanyLandingPageEnabled && rival?.supportedRivalId) {
      return 'landing_page';
    }

    return 'profile';
  }, [
    isCompanyLandingPageEnabled,
    company?.companyData?.forceProfileLinksOnDashboard,
    battlecardsCount,
    companyLinkDefault,
    rival?.supportedRivalId,
  ]);

  const [isLogoHovered, setLogoHovered] = useState(false);
  const isProfileActive = isLogoHovered && defaultRedirect === 'profile';
  const isBattlecardsActive = isLogoHovered && defaultRedirect === 'battlecard';
  // Hide rival links for consumers only - curators always see links
  const hideRivalLinks = !isCurator;

  async function handleUpdateProfile({ isDraft }: { isDraft: boolean }) {
    if (!rival?.profileId || !rival?.id) {
      return;
    }

    try {
      await updateProfileDraftState({
        profileId: rival?.profileId,
        rivalId: rival?.id,
        isDraft,
      });

      notify({
        type: NotifierType.SUCCESS,
        message: t(
          `Dashboard:rivalItem.${
            isDraft ? 'unpublishRivalSuccess' : 'publishRivalSuccess'
          }`,
          {
            rivalName: rival?.name,
          },
        ),
        icon: <Check />,
      });
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: t(
          `Dashboard:rivalItem.${
            isDraft ? 'unpublishRivalError' : 'publishRivalError'
          }`,
          {
            rivalName: rival?.name,
          },
        ),
        icon: <WarningCircle />,
      });
    }
  }

  async function handleUpdateRivalGroup() {
    if (!rivalGroupId || !rival?.id) {
      return;
    }

    try {
      await updateRivalGroup({
        id: rivalGroupId,
        addRivals: [],
        removeRivals: [rival?.id],
      });

      notify({
        type: NotifierType.SUCCESS,
        message: t('Dashboard:rivalItem.removeFromGroupSuccess', {
          rivalName: rival?.name,
        }),
        icon: <Check />,
      });
    } catch (error) {
      notify({
        type: NotifierType.ERROR,
        message: t('Dashboard:rivalItem.removeFromGroupError', {
          rivalName: rival?.name,
        }),
        icon: <WarningCircle />,
      });
    }
  }

  return (
    <StyledMainBox
      width="100%"
      elevation="none"
      background={tokens.color.neutral.white.main}
      round="xxsmall"
      border={{ color: tokens.color.neutral.lightgrey.main, size: 'xsmall' }}
      style={{
        position: 'relative',
      }}
      data-test-id={TEST_IDS.dashboard.rivalItem.mainContainer}
    >
      <Link
        to={getRivalRedirectLink(
          rival?.profileId,
          defaultRedirect,
          rival?.supportedRivalId,
        )}
        onFocus={() => setIndex(100)}
        onBlur={() => setIndex(undefined)}
        onMouseOver={() => setLogoHovered(true)}
        onMouseLeave={() => setLogoHovered(false)}
        style={{
          textDecoration: 'none',
          zIndex,
        }}
        tabIndex={0}
        data-test-id={TEST_IDS.dashboard.rivalItem.container}
        data-tracking-id={TRACKING_IDS.dashboard.rivalItem.button}
      >
        <Box
          justify="center"
          align="center"
          gap="medium"
          height={HEIGHT_WHEN_RIVAL_LINK_HIDDEN}
          pad={{ vertical: 'large', horizontal: 'large' }}
          border={{
            side: 'bottom',
            size: 'xsmall',
            color: tokens.color.neutral.lightgrey.main,
          }}
          data-test-id={TEST_IDS.dashboard.rivalItem.logoContainer}
          style={{ opacity: rival?.isDraft ? 0.4 : 1 }}
        >
          <Logo
            a11yTitle={rival.name}
            data-test-id={TEST_IDS.dashboard.rivalItem.avatar}
            size="xxlarge"
            src={rival?.iconUrl}
          />
          <Text
            size="large"
            truncate
            fontWeight="semibold"
            lineHeight="normal"
            title={rival.name}
            data-test-id={TEST_IDS.dashboard.rivalItem.name}
          >
            {rival.name}
          </Text>
        </Box>
      </Link>
      {isCurator ? (
        <Box
          direction="row"
          fill="horizontal"
          style={{ position: 'absolute', top: 0, left: 0, zIndex: '101' }}
          tabIndex={-1}
        >
          {rival.isDraft ? (
            <Box
              data-test-id={TEST_IDS.dashboard.rivalItem.draftTag}
              margin={{ left: 'small', top: 'medium' }}
            >
              <Badge
                size="small"
                emphasis="high"
                color="grey"
                label={t('Dashboard:rivalItem.draftTag')}
              />
            </Box>
          ) : null}
          <Box margin={{ left: 'auto', top: 'xsmall', right: 'xsmall' }}>
            <ActionMenu
              a11yTitle="Rival menu"
              alignSelf="end"
              tipText=""
              icon={<DotsThreeOutlineVertical weight="fill" />}
              data-test-id={TEST_IDS.dashboard.rivalItem.actionMenu}
              itemProps={{
                disabled: isUpdatingProfile || isUpdatingRivalGroup,
              }}
              data={[
                ...(!!rivalGroupId
                  ? [
                      {
                        key: 'remove-from-group',
                        label: t('Dashboard:rivalItem.removeFromGroup'),
                        testId:
                          TEST_IDS.dashboard.rivalItem.removeFromGroupButton,
                        onClick: () => handleUpdateRivalGroup(),
                      },
                    ]
                  : []),
                ...(rival?.isDraft
                  ? [
                      {
                        key: 'publish',
                        label: t('Dashboard:rivalItem.publish'),
                        testId: TEST_IDS.dashboard.rivalItem.publishButton,
                        onClick: () =>
                          handleUpdateProfile({
                            isDraft: false,
                          }),
                      },
                    ]
                  : [
                      {
                        key: 'unpublish',
                        label: t('Dashboard:rivalItem.unpublish'),
                        testId: TEST_IDS.dashboard.rivalItem.unpublishButton,
                        onClick: () =>
                          handleUpdateProfile({
                            isDraft: true,
                          }),
                      },
                    ]),
              ]}
            />
          </Box>
        </Box>
      ) : null}

      {!hideRivalLinks && (
        <Box direction="row">
          <RivalGroupActionItem
            isLoading={false}
            label={
              battlecardsCount
                ? t('Dashboard:rivalItem.battlecard', {
                    count: battlecardsCount,
                  })
                : t('Dashboard:rivalItem.battlecard_empty')
            }
            count={battlecardsCount}
            href={getRivalRedirectLink(rival?.profileId, 'battlecard')}
            data-test-id={TEST_IDS.dashboard.rivalItem.battlecardLink}
            data-tracking-id={
              TRACKING_IDS.dashboard.rivalItem.battlecardsButton
            }
            round={{ size: 'xxsmall', corner: 'bottom-left' }}
            active={isBattlecardsActive}
            disabled={!battlecardsCount}
          />
          <RivalGroupActionItem
            isLoading={false}
            round={{ size: 'xxsmall', corner: 'bottom-right' }}
            label={
              cardsCount
                ? t('Dashboard:rivalItem.profile', { count: cardsCount })
                : t('Dashboard:rivalItem.profile_empty')
            }
            count={cardsCount}
            href={getRivalRedirectLink(rival?.profileId, 'profile')}
            separator
            data-test-id={TEST_IDS.dashboard.rivalItem.profileLink}
            data-tracking-id={TRACKING_IDS.dashboard.rivalItem.profileButton}
            active={isProfileActive}
          />
        </Box>
      )}
    </StyledMainBox>
  );
}

function RivalGroupItemSkeleton() {
  return (
    <StyledMainBox
      width="100%"
      elevation="none"
      background={tokens.color.neutral.white.main}
      round="xxsmall"
      border={{ color: tokens.color.neutral.lightgrey.main, size: 'xsmall' }}
      style={{
        position: 'relative',
        pointerEvents: 'none',
      }}
      data-test-id={TEST_IDS.dashboard.rivalItem.mainContainer}
    >
      <Box
        justify="center"
        align="center"
        gap="medium"
        height={HEIGHT_WHEN_RIVAL_LINK_HIDDEN}
        pad={{ vertical: 'large', horizontal: 'large' }}
        border={{
          side: 'bottom',
          size: 'xsmall',
          color: tokens.color.neutral.lightgrey.main,
        }}
        data-test-id={TEST_IDS.dashboard.rivalItem.logoContainer}
      >
        <Box width="64px" height="64px" alignSelf="center">
          <Skeleton.Square size="100%" />
        </Box>
        <Box width="medium" pad={{ horizontal: 'xxlarge' }}>
          <Skeleton.Line numberOfLines={1} />
        </Box>
      </Box>
      <Box direction="row">
        <RivalGroupActionItem
          isLoading={true}
          label=""
          count={0}
          href=""
          round={{ size: 'xxsmall', corner: 'bottom-left' }}
        />
        <RivalGroupActionItem
          isLoading={true}
          label=""
          count={0}
          href=""
          round={{ size: 'xxsmall', corner: 'bottom-right' }}
          separator
        />
      </Box>
    </StyledMainBox>
  );
}

RivalGroupItem.Skeleton = RivalGroupItemSkeleton;
